import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Permissions } from '../typings/user';
import getWabaLevelUserPermissions from '../requests/get-waba-level-permissions';
import { WabaLevelUserPermissionsResponse } from '../typings/waba-level-permission';

export const useWabaLevelPermission = () => {
  const [data, setData] = useState<WabaLevelUserPermissionsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetchedRef = useRef<boolean>(false);

  const fetchPermissions = useCallback(async () => {    
    try {
      setLoading(true);
      setError(null);
      const result = await getWabaLevelUserPermissions();
      setData(result);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPermissions();
    }
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (integrationId: string, permissionKey: Permissions): { 
      hasAccess: boolean; 
      loading: boolean;
      error: Error | null;
    } => {
      if (loading) return { hasAccess: false, loading: true, error: null };
      if (error) return { hasAccess: false, loading: false, error };
      if (!data) return { hasAccess: false, loading: false, error: null };

      const record = data.access[integrationId];
      if (!record) return { hasAccess: false, loading: false, error: null };

      return { 
        hasAccess: record.permissions?.includes(permissionKey) ?? false,
        loading: false,
        error: null
      };
    },
    [data, loading, error]
  );

  const getWabasWithPermission = useCallback(
    (permissionKey: Permissions): { 
      wabas: { wabaId: string; roles: string[] }[];
      loading: boolean;
      error: Error | null;
    } => {
      if (loading) return { wabas: [], loading: true, error: null };
      if (error) return { wabas: [], loading: false, error };
      if (!data || !data.access) return { wabas: [], loading: false, error: null };
      
      const wabas = Object.entries(data.access)
        .filter(([_, record]) => record.permissions?.includes(permissionKey))
        .map(([wabaId, record]) => ({
          wabaId,
          roles: record.roles
        }));

      return {
        wabas,
        loading: false,
        error: null
      };
    },
    [data, loading, error]
  );

  const value = useMemo(
    () => ({ hasPermission, getWabasWithPermission, refresh: fetchPermissions, loading, error }),
    [hasPermission, getWabasWithPermission, fetchPermissions, loading, error]
  );

  return value;
};

export default useWabaLevelPermission;

